/**
 * Toll-collection points already attached to a Mapbox Directions route.
 *
 * These are better than a proximity POI search: each point belongs to the
 * exact carriageway the selected route drives through, and the route response
 * is already in memory. Every `toll_collection` occurrence remains an event:
 * coordinate deduplication would erase a real second charge when an
 * out-and-back route passes through the same plaza twice. Exact overlaps are
 * harmless on the map because their badges render on top of one another.
 */

import type { LngLat, Route } from '../core/types';

export interface RouteTollPoint {
  location: LngLat;
  /** Mapbox currently emits toll_booth or toll_gantry; keep future values. */
  type: string;
  name: string | null;
}

export function routeTollPoints(route: Route | null | undefined): RouteTollPoint[] {
  if (!route) return [];

  const points: RouteTollPoint[] = [];
  for (const leg of route.legs) {
    for (const step of leg.steps) {
      for (const intersection of step.intersections ?? []) {
        const toll = intersection.toll_collection;
        if (!toll) continue;
        const location = intersection.location;
        if (
          !Array.isArray(location) ||
          location.length < 2 ||
          !Number.isFinite(location[0]) ||
          !Number.isFinite(location[1])
        ) {
          continue;
        }

        const type = (toll.type ?? '').trim() || 'toll_booth';
        const name = (toll.name ?? '').trim() || null;
        points.push({ location: [location[0], location[1]], type, name });
      }
    }
  }
  return points;
}
